import { useAuth } from '@/auth/useAuth';
import { Button } from '@/ui/Button';
import { Breadcrumb } from './Breadcrumb';
import { GlobalSearch } from './GlobalSearch';
import { NewMenu } from './NewMenu';

/** The assistant's toggle, so that closing the drawer can put focus back on it. */
export const ASSISTANT_TOGGLE_ID = 'assistant-toggle';

export interface CommandStripProps {
  /** Whether the assistant drawer is open, for the toggle's state. */
  readonly assistantOpen: boolean;
  /** False hides the toggle altogether, as when the workspace has the assistant switched off. */
  readonly assistantAvailable: boolean;
  /** Opens or closes the assistant drawer. */
  readonly onToggleAssistant: () => void;
}

/**
 * The strip above the working surface: where you are, how to find anything, and what you can
 * start or summon.
 *
 * Workspace breadcrumbs establish context. The search box moves between workflows and jumps
 * straight to a record, and "New" starts a job, lead, customer or invoice from wherever you are,
 * so that neither needs the rail.
 *
 * The assistant toggle lives here, at the edge of the chrome and away from the record, so that
 * summoning the assistant is always the same gesture on every screen and never sits inside the
 * data it comments on. It is not drawn when the assistant is not available: a control that leads
 * only to an apology is clutter.
 *
 * @param props see {@link CommandStripProps}
 * @returns the command strip
 */
export function CommandStrip({
  assistantOpen,
  assistantAvailable,
  onToggleAssistant,
}: CommandStripProps): React.JSX.Element {
  const { identity } = useAuth();

  return (
    <div className="fo-strip">
      <Breadcrumb />
      {/* Keyed on the principal, so what one person searched for is never left for the next. */}
      <GlobalSearch key={identity ?? 'signed-out'} />
      <div className="fo-strip__actions">
        <NewMenu />
        {assistantAvailable && (
          <Button
            id={ASSISTANT_TOGGLE_ID}
            variant={assistantOpen ? 'secondary' : 'ghost'}
            size="sm"
            icon="assistant"
            aria-expanded={assistantOpen}
            aria-controls="assistant-drawer"
            onClick={onToggleAssistant}
          >
            Assistant
          </Button>
        )}
      </div>
    </div>
  );
}
